import { useEffect, useMemo, useRef } from 'react';
import { useConsolidatedPo } from '@/hooks/use-packing';
import { formatDateTime } from '@/lib/utils';

// Printable consolidated PO for the warehouse packing team. Opened in a named
// tab from the Packing tab with `?loc=<warehouseLocationId>&ids=<poId,poId,…>`
// in the hash query. Read-only: it re-requests the merge from the API so the
// printout always reflects current PO contents.
export function PackingConsolidatedPrintPage() {
  const { loc, ids } = useMemo(() => {
    const query = window.location.hash.split('?')[1] ?? '';
    const params = new URLSearchParams(query);
    return {
      loc: params.get('loc') ?? undefined,
      ids: (params.get('ids') ?? '').split(',').filter(Boolean),
    };
  }, []);

  const { data, isLoading, isError } = useConsolidatedPo(loc, ids);
  const merged = data?.data;

  // Same guard as the PO print page: the named tab re-mounts this component
  // on reuse, which would re-fire window.print() without the ref.
  const hasPrintedRef = useRef(false);
  useEffect(() => {
    if (merged && !hasPrintedRef.current) {
      hasPrintedRef.current = true;
      setTimeout(() => window.print(), 500);
    }
  }, [merged]);

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  if (isError || !merged) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Couldn't load the consolidated purchase order. It may include orders that are no longer open.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-8 print:p-4">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">Consolidated Purchase Order — Packing List</h1>
          <p className="text-sm text-muted-foreground">
            {merged.warehouseLocationName} · Generated {formatDateTime(merged.generatedAt)}
          </p>
        </div>
        <div className="text-right text-sm text-muted-foreground">
          <div>{merged.pos.length} purchase orders</div>
          <div>{merged.totalProducts} products</div>
        </div>
      </div>

      <div className="mb-6 rounded-lg border border-border p-4">
        <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Included Purchase Orders</h3>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          {merged.pos.map((po) => (
            <span key={po.id}>
              PO #{po.orderNumber} — {po.locationName}
              {po.payeeName ? ` (${po.payeeName})` : ''}
            </span>
          ))}
        </div>
      </div>

      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b-2 border-border">
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Product</th>
            <th className="py-2 text-left text-xs font-semibold uppercase text-muted-foreground">Unit</th>
            <th className="py-2 text-right text-xs font-semibold uppercase text-muted-foreground">Total Qty</th>
            <th className="py-2 pl-6 text-left text-xs font-semibold uppercase text-muted-foreground">
              Pack Per Location
            </th>
          </tr>
        </thead>
        <tbody>
          {merged.items.map((item) => (
            <tr
              key={`${item.productId}-${item.unitOfMeasure}`}
              className="break-inside-avoid border-b border-border align-top"
            >
              <td className="py-2">
                <div className="font-medium">{item.productName}</div>
                {item.productBarcode && <div className="text-xs text-muted-foreground">{item.productBarcode}</div>}
              </td>
              <td className="py-2 text-sm">{item.unitOfMeasure}</td>
              <td className="py-2 text-right text-sm font-semibold">{item.totalQuantity}</td>
              <td className="py-2 pl-6 text-sm">
                {item.breakdown.map((b) => (
                  <div key={`${b.poId}-${b.locationId}`}>
                    {b.locationName}: <span className="font-medium">{b.quantity}</span>{' '}
                    <span className="text-xs text-muted-foreground">(PO #{b.orderNumber})</span>
                  </div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="mt-6 text-xs text-muted-foreground">
        Packing reference only — quantities cannot be edited here and the source purchase orders are unchanged.
      </p>

      <div className="mt-8 print:hidden">
        <button
          onClick={() => window.print()}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Print
        </button>
        <button
          onClick={() => window.close()}
          className="ml-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Close
        </button>
      </div>
    </div>
  );
}
